/**
 * 写请求的**载荷构造（纯函数）**：把界面上的表单值变成后端真正要的形状。
 *
 * ## 为什么要单独一层（而不是在页面里内联写对象字面量）
 *
 * 因为这一层有三个**静默出错**的坑，而且三者都只在真机上才暴露：
 *
 * 1. **可选项必须"不发"，而不是发空串**。
 *    `gradeCode: ''` 会被 zod 拒（`''` 不是合法年级），而 `lessonIds: []` 更糟：
 *    后端把它当"**整组替换**"，于是"编辑一下备注"会把家长关联好的课文**全部清空**。
 *    所以这里的规则是：**字段值不确定就不出现这个键**。
 *
 * 2. **拼音的"留空"有两种含义，取决于动作**：
 *    · 新建时留空 = 用内置字表自动生成 → **不发 `pinyin`**；
 *    · 改拼音时留空 = **恢复自动值** → 必须发 `{"pinyin": ""}`（或 `null`），
 *      让后端把 `pinyin_overridden` 落回 false。
 *    两者都用"空"表达，靠"有没有这个键"区分。搞反了家长就再也回不到自动读音。
 *
 * 3. **编辑时不能改身份字段**：`charText` / `wordText` / `textbookId` 不在
 *    `update*Schema` 里，多传会被 zod 拒（`.strict()` 不是默认，但显式不传更稳）。
 *
 * 这些都能被纯函数单测覆盖，所以不留在页面里（页面里只能靠"点一遍看看"）。
 *
 * ⚠️ **它只服务于管理后台**（构造的是**维护类**请求的载荷）。
 *   家长端**不再有任何写请求** —— 所以本文件不该被家长端页面 import。
 */
import type {
  CreateHanziPayload,
  CreateLessonPayload,
  CreateWordPayload,
  ImportPayload,
  ListImportPayload,
  UpdateHanziPayload,
  UpdateLessonPayload,
  UpdateWordPayload,
} from './api/dictationAdmin';

/** 表单里"可为空"的字符串：`''` = 没填。 */
type OptionalText = string;

/** 只在有值时出现这个键（`''` / `null` / `undefined` 都不算"有值"）。 */
function withIfFilled<T extends object>(base: T, key: string, value: OptionalText | null | undefined): T {
  const trimmed = typeof value === 'string' ? value.trim() : value;
  if (trimmed === null || trimmed === undefined || trimmed === '') return base;
  return { ...base, [key]: trimmed };
}

// ---------------------------------------------------------------- 导入

/**
 * 导入预览/提交的请求体。
 *
 * ⚠️ 这是"预览与提交必须是同一份输入"的**唯一构造入口**：页面把结果存进状态机的
 *   `previewPayload`，提交时直接把那一份发出去，绝不重新构造。
 *
 * `text` **原样发出、不 trim**：后端按行解析，行首/行内的空格可能是分隔符或缩进；
 * 前端擅自 trim 会让"我粘的东西"与"程序看到的东西"不是同一份。
 */
export function buildImportRequest(input: {
  text: string;
  textbook: string;
  /**
   * 默认年级 / 学期 —— 课本按册建（一本书 = 一册）。
   *
   * ⚠️ 空串 = 不发这个键（由后端逐行报"缺年级/学期"）。界面上会先拦一道
   *    （见 `validateImportInput`）。
   * ⛔ 版次已经彻底不用（2026-10-18）：不构造、不发。
   */
  gradeCode: string;
  semester: string;
  /** 默认表类型（识字表 / 写字表 / 词语表）；空串 = 不限（不替家长猜一张表）。 */
  listType?: string;
  /** 词条的默认类别；空串 = 交给后端按"词语"处理。 */
  wordCategory?: string;
}): ImportPayload {
  const base: ImportPayload = { text: input.text };
  const withTextbook = withIfFilled(base, 'textbook', input.textbook);
  const withGrade = withIfFilled(withTextbook, 'gradeCode', input.gradeCode);
  const withSemester = withIfFilled(withGrade, 'semester', input.semester);
  /** ⚠️ 空串 = **不发这个键**（"不知道"与"选了某个表"必须分得开）。 */
  const withListType = withIfFilled(withSemester, 'listType', input.listType ?? '');
  return withIfFilled(withListType, 'wordCategory', input.wordCategory ?? '');
}

// ---------------------------------------------------------------- 收录（识字表 / 写字表）

/**
 * **按「书 + 表」导入**的请求体（`POST .../lists/import`）。
 *
 * 与 `buildImportRequest` 的区别：这本书、这一册、这张表是**界面上选好的**，
 * 所以它们**永远显式发出**（不像通用导入那样"没写就不发"）——
 * 少发一个，后端就不知道往哪张表上落，收录会静默丢失。
 *
 * ## 书可以**还没建**
 *
 * 2026-10-18 本轮用户定稿："三格选好就可以录入了；录入并保存之后，
 * 关联到所选出版社、年级和学期的这本书上。" 所以这里也接受
 * `publisher`（人话）—— 那种情况下不传 `textbookId`，后端按
 * 「出版社 + 年级 + 学期」解析，**提交时不存在就建**（预览一行都不写）。
 * ⚠️ 两个都给时只发 `textbookId`（它是唯一确定的那一册，别再让后端去猜）。
 *
 * ⛔ **不发 `wordCategory`**（2026-10-23 用户要求删掉「这些词算什么」下拉）：
 *    录入的词里既有成语也有词语，那个标签本来就不对。不传时后端按默认的
 *    `phrase`（词语）落库 —— 与删界面之前"下拉停在空 = 按词语算"的行为一字不差。
 *    后端的 `words.category` 字段与 schema 都保留（别处仍要读这个标签）。
 */
export function buildListImportRequest(input: {
  textbookId?: string;
  /** 出版社**人话**（书还没建过时用它 + 年级 + 学期让后端解析 / 新建）。 */
  publisher?: string;
  gradeCode: string;
  semester: string;
  listType: string;
  text: string;
  /** 预览里给重复出现的字选的读音（迁移 0018）。 */
  readings?: { line: number; index: number; reading: string }[];
  dryRun: boolean;
}): ListImportPayload {
  const textbookId = input.textbookId?.trim() ?? '';
  const publisher = input.publisher?.trim() ?? '';
  const base: ListImportPayload = {
    ...(textbookId !== '' ? { textbookId } : {}),
    ...(textbookId === '' && publisher !== '' ? { publisher } : {}),
    gradeCode: input.gradeCode,
    semester: input.semester,
    listType: input.listType,
    text: input.text,
    /** 标音只对重复出现的字有意义；空数组 = 不发这个键。 */
    ...(input.readings !== undefined && input.readings.length > 0
      ? { readings: input.readings }
      : {}),
    dryRun: input.dryRun,
  };
  return base;
}

// ---------------------------------------------------------------- 课文

export function buildCreateLessonPayload(input: {
  textbookId: string;
  gradeCode: string;
  semester: string;
  title: string;
  lessonLabel: string;
  lessonNo: number | null;
  unitNo: number | null;
}): CreateLessonPayload {
  let payload: CreateLessonPayload = {
    textbookId: input.textbookId,
    gradeCode: input.gradeCode,
    semester: input.semester,
    title: input.title.trim(),
  };
  payload = withIfFilled(payload, 'lessonLabel', input.lessonLabel);
  // `lessonNo` 留空 = 让后端从课序写法里取数字（`extractLessonNo`），所以**不发这个键**
  if (input.lessonNo !== null && Number.isFinite(input.lessonNo)) {
    payload = { ...payload, lessonNo: input.lessonNo };
  }
  return { ...payload, unitNo: input.unitNo };
}

export function buildUpdateLessonPayload(input: {
  gradeCode: string;
  semester: string;
  title: string;
  lessonLabel: string;
  lessonNo: number | null;
  unitNo: number | null;
}): UpdateLessonPayload {
  let payload: UpdateLessonPayload = {
    gradeCode: input.gradeCode,
    semester: input.semester,
    title: input.title.trim(),
  };
  payload = withIfFilled(payload, 'lessonLabel', input.lessonLabel);
  if (input.lessonNo !== null && Number.isFinite(input.lessonNo)) {
    payload = { ...payload, lessonNo: input.lessonNo };
  }
  return { ...payload, unitNo: input.unitNo };
}

// ---------------------------------------------------------------- 字 / 词

export interface ItemFormValues {
  gradeCode: string | null;
  semester: string | null;
  /**
   * ⚠️ 字与词**共用这个字段，含义不同**（第五步改造后）：
   *   · 字（hanzi）→ **表类型**（`shizi` / `xiezi`）；
   *   · 词（word）→ **词语类别**（`common` / `phrase` / `idiom`）。
   */
  category: string;
  note: string;
  fromTextbook: boolean;
  /** `undefined` = **不动关联**（查不到时界面会这样传）；数组 = 整组替换。 */
  lessonIds?: string[];
  /**
   * **逐位读音**（只有词有意义）：每个字在这个词里读哪个音。
   * `undefined` = 不动（从整词拼音推）；数组 = 整组替换。
   */
  charReadings?: string[];
}

export function buildCreateHanziPayload(
  input: ItemFormValues & { textbookId: string; charText: string; pinyin: string },
): CreateHanziPayload {
  // 拼音留空 = 用自动值（**不发这个键**）
  const base: CreateHanziPayload = {
    textbookId: input.textbookId,
    charText: input.charText.trim(),
    /** 新建字时的"收录到哪张表"（第五步：`category` 在字上就是表类型）。 */
    ...(input.category === '' ? {} : { listType: input.category }),
    gradeCode: input.gradeCode,
    semester: input.semester,
    /** 收录的册别与字本身同一册（界面上一处选、两处用）。 */
    ...(input.gradeCode !== null ? { listGradeCode: input.gradeCode } : {}),
    ...(input.semester !== null ? { listSemester: input.semester } : {}),
    note: input.note,
    ...(input.lessonIds === undefined ? {} : { lessonIds: input.lessonIds }),
  };
  return input.pinyin.trim() === '' ? base : { ...base, pinyin: input.pinyin.trim() };
}

export function buildUpdateHanziPayload(input: ItemFormValues & { pinyin: string }): UpdateHanziPayload {
  /**
   * ⚠️ 编辑字时**不发 `category`**：`hanzi` 上没有这一列了（迁移 0009 删掉），
   *    "这个字在哪张表上"要通过 `PUT .../hanzi/:id/listings`（整组替换收录）。
   *    发一个不存在的键会被 zod 拒，而"发了但没生效"更糟。
   */
  const base: UpdateHanziPayload = {
    gradeCode: input.gradeCode,
    semester: input.semester,
    note: input.note,
    ...(input.lessonIds === undefined ? {} : { lessonIds: input.lessonIds }),
  };
  // ⚠️ 编辑时留空 = **清除人工覆盖**（恢复自动）。这里用显式空串，而不是"不发"：
  //    不发的话后端保持原来的覆盖值，家长就会以为"改回自动"没生效。
  return { ...base, pinyin: input.pinyin.trim() };
}

export function buildCreateWordPayload(
  input: ItemFormValues & { textbookId: string; wordText: string; pinyin: string },
): CreateWordPayload {
  const base: CreateWordPayload = {
    textbookId: input.textbookId,
    wordText: input.wordText.trim(),
    category: input.category,
    fromTextbook: input.fromTextbook,
    gradeCode: input.gradeCode,
    semester: input.semester,
    note: input.note,
    ...(input.lessonIds === undefined ? {} : { lessonIds: input.lessonIds }),
    ...(input.charReadings === undefined ? {} : { charReadings: input.charReadings }),
  };
  return input.pinyin.trim() === '' ? base : { ...base, pinyin: input.pinyin.trim() };
}

export function buildUpdateWordPayload(input: ItemFormValues & { pinyin: string }): UpdateWordPayload {
  const base: UpdateWordPayload = {
    category: input.category,
    fromTextbook: input.fromTextbook,
    gradeCode: input.gradeCode,
    semester: input.semester,
    note: input.note,
    ...(input.lessonIds === undefined ? {} : { lessonIds: input.lessonIds }),
    ...(input.charReadings === undefined ? {} : { charReadings: input.charReadings }),
  };
  return { ...base, pinyin: input.pinyin.trim() };
}

/**
 * 「只改拼音」的请求体。
 *
 * ⚠️ 空串 = **恢复自动值**（后端据此把 `pinyin_overridden` 落回 false），
 *   ⛔ 不是"把这个字读成空"。界面上的「恢复自动」按钮就走这里。
 */
export function buildPinyinOverride(input: { pinyin: string }): { pinyin: string } {
  return { pinyin: input.pinyin.trim() };
}
